import { jsx as _jsx } from "react/jsx-runtime";
import { addErrorNotification, addOrderGiftCardPayment, getOrderUnpaidBalance, hasOrderBeenCreated, orderHasInProgressAction, orderRequiresPayment } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { observer } from 'mobx-react';
import { forwardRef, useMemo, useReducer } from 'react';
import { useFormValidator } from '../../hooks/use-form-validator';
import { Form } from '../../subcomponents/form/form/form';
import { withComponentWrapper } from '../../utils/with-component-wrapper';
import { GiftCardPaymentFormActionType as ActionType } from './types/action-type';
import { GiftCardPaymentFormContextInstance } from './types/context';
import { defaultGiftCardPaymentFormProps, giftCardPaymentFormPropsValidator } from './types/props';
import { initialGiftCardPaymentFormState } from './types/state';
import { giftCardPaymentFormActionReducer } from './utils/action-reducer';
import { getNotificationDetailsForError } from './utils/get-notification-details-for-error';
const b = bemClassBuilder('v-gift-card-payment-form');
const GiftCardPaymentFormBase = (props, ref) => {
    const { displayText, config, events, formId, children } = props;
    const [state, dispatch] = useReducer(giftCardPaymentFormActionReducer, initialGiftCardPaymentFormState);
    const { validationResult, validator } = useFormValidator(config.validation);
    const { formData, helpTextsById } = state;
    const hasNoPendingAmount = getOrderUnpaidBalance() === 0;
    const disabled = hasNoPendingAmount || orderHasInProgressAction() || state.isSubmitting;
    const context = useMemo(() => ({
        onChange: (fieldName, value) => {
            dispatch({ type: ActionType.SetFormData, formData: Object.assign(Object.assign({}, formData), { [fieldName]: value }) });
        },
        setHelpText: (helpText) => dispatch({ type: ActionType.SetHelpText, helpText }),
        formData,
        validationResult,
        validator,
        disabled,
        loading: false,
        config
    }), [config, disabled, formData, validationResult, validator]);
    const handleSubmit = async () => {
        await addOrderGiftCardPayment(Object.assign(Object.assign({}, formData), { pin: formData.pin || undefined }));
        dispatch({ type: ActionType.SuccessfullyAddedGiftCard });
    };
    const handleFailedSubmit = error => {
        var _a;
        addErrorNotification(getNotificationDetailsForError(displayText, error), error);
        (_a = events.onAfterFailedSubmit) === null || _a === void 0 ? void 0 : _a.call(events, error);
    };
    const handleFormSubmittingStateChanged = (isSubmitting) => {
        var _a;
        dispatch({ type: ActionType.SetFormSubmittingState, isSubmitting });
        (_a = events.onFormSubmittingStateChanged) === null || _a === void 0 ? void 0 : _a.call(events, isSubmitting);
    };
    if (!hasOrderBeenCreated() || !orderRequiresPayment())
        return null;
    return (_jsx(Form, Object.assign({}, props, { id: formId, className: b.block(), disabled: disabled, validator: () => validator.validateForm(formData), helpTexts: Object.values(helpTextsById), events: Object.assign(Object.assign({}, events), { onSubmit: handleSubmit, onAfterFailedSubmit: handleFailedSubmit, onFormSubmittingStateChanged: handleFormSubmittingStateChanged }), setFormData: getMergedData => {
            dispatch({ type: ActionType.SetFormData, formData: getMergedData(formData) });
        }, ref: ref }, { children: _jsx(GiftCardPaymentFormContextInstance.Provider, Object.assign({ value: context }, { children: children })) })));
};
export const GiftCardPaymentForm = observer(forwardRef(GiftCardPaymentFormBase));
GiftCardPaymentForm.displayName = 'GiftCardPaymentForm';
/**
 * The **Gift Card Payment Form** component allows gift cards to be applied to an in-progress order.
 */
export const GiftCardPaymentFormComponent = withComponentWrapper(GiftCardPaymentForm, defaultGiftCardPaymentFormProps, giftCardPaymentFormPropsValidator);
